import { useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { REACTIONS } from '../../utils/constants';
import { alpha, tokens } from '../../theme/tokens';
import { useAuth } from '../../hooks/useAuth';

/**
 * Facebook-style engagement row:
 * tap Like → default 👍 (or toggle off)
 * long-press / hover → emoji picker
 */
export function ReactionBar({
  myReaction,
  reactionCounts = {},
  shareCount = 0,
  onReact,
  onShare,
  compact = false,
  disabled = false,
}) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const holdTimer = useRef(null);
  const rootRef = useRef(null);
  const active = Boolean(myReaction);

  useEffect(() => {
    if (!open) return undefined;
    function onDoc(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('pointerdown', onDoc);
    return () => document.removeEventListener('pointerdown', onDoc);
  }, [open]);

  function clearHold() {
    if (holdTimer.current) {
      window.clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
  }

  function startHold() {
    clearHold();
    holdTimer.current = window.setTimeout(() => {
      setOpen(true);
    }, 380);
  }

  function onLikeClick() {
    clearHold();
    if (open) {
      setOpen(false);
      return;
    }
    onReact?.(active ? null : '👍');
  }

  function pickEmoji(emoji) {
    clearHold();
    setOpen(false);
    onReact?.(myReaction === emoji ? null : emoji);
  }

  const total =
    Object.values(reactionCounts).reduce((sum, n) => sum + (Number(n) || 0), 0) ||
    0;

  return (
    <Stack
      ref={rootRef}
      direction="row"
      spacing={1}
      alignItems="center"
      useFlexGap
      flexWrap="wrap"
      sx={{
        position: 'relative',
        my: compact ? 0.75 : 1.25,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
      }}
    >
      {open && (
        <Box
          sx={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            mb: 0.75,
            zIndex: 5,
            display: 'flex',
            gap: 0.5,
            p: 0.75,
            borderRadius: 999,
            bgcolor: tokens.surface,
            border: `1px solid ${tokens.border}`,
            boxShadow: `0 12px 28px ${alpha(tokens.shellDeep, 0.28)}`,
          }}
        >
          {REACTIONS.map((emoji) => (
            <IconButton
              key={emoji}
              size="small"
              onClick={() => pickEmoji(emoji)}
              aria-label={`React ${emoji}`}
              sx={{
                fontSize: compact ? '1.15rem' : '1.35rem',
                width: compact ? 36 : 42,
                height: compact ? 36 : 42,
                bgcolor:
                  myReaction === emoji ? alpha(tokens.brand, 0.14) : 'transparent',
                '&:hover': {
                  transform: 'translateY(-4px) scale(1.12)',
                  bgcolor: alpha(tokens.brand, 0.1),
                },
                transition: 'transform 160ms ease, background-color 160ms ease',
              }}
            >
              {emoji}
            </IconButton>
          ))}
        </Box>
      )}

      <Button
        size="small"
        variant={active ? 'contained' : 'outlined'}
        color={active ? 'secondary' : 'primary'}
        startIcon={
          active ? (
            <Typography component="span" sx={{ fontSize: '1rem', lineHeight: 1 }}>
              {myReaction}
            </Typography>
          ) : (
            <ThumbUpAltOutlinedIcon sx={{ fontSize: 18 }} />
          )
        }
        disabled={disabled}
        onClick={onLikeClick}
        onPointerDown={startHold}
        onPointerUp={clearHold}
        onPointerLeave={clearHold}
        onPointerCancel={clearHold}
        onContextMenu={(e) => {
          e.preventDefault();
          setOpen(true);
        }}
        aria-label={active ? 'Liked' : 'Like'}
        title={user ? 'Press and hold to pick a reaction' : undefined}
        sx={{
          minHeight: 40,
          px: 1.5,
          fontWeight: 600,
          fontSize: { xs: '0.875rem', sm: '0.9375rem' },
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        {active ? 'Liked' : 'Like'}
        {total > 0 ? ` · ${total}` : ''}
      </Button>

      <Button
        size="small"
        variant="outlined"
        startIcon={<ShareOutlinedIcon sx={{ fontSize: 18 }} />}
        disabled={disabled}
        onClick={() => onShare?.()}
        sx={{
          minHeight: 40,
          px: 1.5,
          fontWeight: 600,
          fontSize: { xs: '0.875rem', sm: '0.9375rem' },
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        Share · {Number(shareCount) || 0}
      </Button>
    </Stack>
  );
}
